import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Button,
  Divider,
  MenuItem,
  Menu,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  CircularProgress,
  Tooltip,
  TablePagination,
  Link,
  useMediaQuery,
  Avatar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  getOrders,
  getOrder,
  updateOrder,
  listOrderTypes,
  listOrderStatusDefinitions,
  softDeleteOrder,
} from '../../../app/api';
import { buildOrderUpdatePayloadFromOrderDetail } from '../private/buildOrderUpdatePayload';
import { migrateOrderDraftMediaToHistory } from '../private/migrateOrderDraftMediaToHistory';
import ConfirmDialog from '../../core/public/ConfirmDialog';
import ListToolbar from '../../core/public/ListToolbar';
import { getUserPreferences, updateUserPreferences } from '../../../app/userPreferences';
import { useTranslation } from '../../../app/LanguageContext';
import { useWorkspace } from '../../../app/WorkspaceContext';
import { getLastUsedOrderTypeId, pickActiveOrderTypeId } from '../private/lastOrderTypeStorage';
import { formatDateTime, formatDate } from '../../../app/date';
import { formatCurrency } from '../../../app/currency';
import { statusColorChipSx } from '../../../app/statusColorChip';
import { PRODUCT_DEFAULT_IMAGE } from '../../medias/public/MediaGallery';

const ORDERS_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const statusColors = {
  DRAFT: 'default',
  SENT: 'info',
  APPROVED: 'success',
  CONFIRMED: 'success',
  IN_PREPARATION: 'info',
  DELIVERED: 'primary',
  REJECTED: 'error',
  EXPIRED: 'warning',
  CANCELLED: 'error',
};

// null = "não definido pelo usuário" (mostra todos); array = seleção explícita
const parseStoredFilter = (stored) => {
  if (!stored) return null;
  try {
    const arr = JSON.parse(stored);
    if (!Array.isArray(arr)) return null;
    return arr;
  } catch {
    return null;
  }
};

const serializeFilter = (arr) => {
  if (arr === null) return '';
  return JSON.stringify(arr);
};

const LIST_SORT_PREF_KEY = 'orders.list_sort';
const SEARCH_DEBOUNCE_MS = 300;

const parseStoredListSort = (stored) => {
  if (!stored || typeof stored !== 'string') {
    return { sort: '', order: 'desc' };
  }
  try {
    const obj = JSON.parse(stored);
    if (!obj || typeof obj !== 'object') {
      return { sort: '', order: 'desc' };
    }
    const sort = typeof obj.sort === 'string' ? obj.sort : '';
    const order = obj.order === 'asc' || obj.order === 'desc' ? obj.order : 'desc';
    if (sort !== '' && sort !== 'delivery_date') {
      return { sort: '', order: 'desc' };
    }
    return { sort, order };
  } catch {
    return { sort: '', order: 'desc' };
  }
};

const serializeListSort = (s) => {
  if (!s.sort) {
    return '';
  }
  return JSON.stringify({ sort: s.sort, order: s.order });
};

const orderEditCellLinkSx = {
  display: 'block',
  px: 2,
  py: 2,
  cursor: 'pointer',
  color: 'inherit',
  '&:hover': {
    color: 'primary.main',
  },
  '&:focus-visible': {
    borderRadius: 1,
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
};

const statusFilterValueFromDefinition = (d) => {
  const leg = d?.legacy_code;
  if (leg && String(leg).trim()) {
    return String(leg).trim();
  }
  return d.id;
};

const ORDERS_TOOLBAR_BUTTON_SX = {
  height: 36,
  minHeight: 36,
  boxSizing: 'border-box',
};

const OrdersPage = () => {
  const { translate, language } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusDefinitions, setStatusDefinitions] = useState([]);
  const [listSort, setListSort] = useState({ sort: '', order: 'desc' });
  const [selectedStatuses, setSelectedStatuses] = useState(null);
  const [statusFilterPrefsReady, setStatusFilterPrefsReady] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [pendingSoftDelete, setPendingSoftDelete] = useState(null);
  const [statusQuickMenu, setStatusQuickMenu] = useState(null);
  const [statusQuickDetail, setStatusQuickDetail] = useState(null);
  const [statusQuickLoading, setStatusQuickLoading] = useState(false);
  const [statusQuickSaving, setStatusQuickSaving] = useState(false);
  const [activeOrderTypes, setActiveOrderTypes] = useState([]);
  const [orderTypesLoading, setOrderTypesLoading] = useState(true);
  const [newOrderMenuAnchor, setNewOrderMenuAnchor] = useState(null);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const savePrefsTimeout = useRef(null);
  const saveListSortTimeout = useRef(null);
  const searchDebounceRef = useRef(null);

  const defaultNewOrderTypeId = useMemo(() => {
    if (!activeOrderTypes.length) return '';
    const last = getLastUsedOrderTypeId(currentWorkspace?.id);
    return pickActiveOrderTypeId(activeOrderTypes, null, last);
  }, [activeOrderTypes, currentWorkspace?.id]);

  const primaryOrderTypes = useMemo(() => activeOrderTypes.slice(0, 3), [activeOrderTypes]);
  const overflowOrderTypes = useMemo(() => activeOrderTypes.slice(3), [activeOrderTypes]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrderTypesLoading(true);
      try {
        const tr = await listOrderTypes(true);
        if (!cancelled) setActiveOrderTypes(tr.data || []);
      } catch (_) {
        if (!cancelled) setActiveOrderTypes([]);
      } finally {
        if (!cancelled) setOrderTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listOrderStatusDefinitions();
        if (!cancelled) setStatusDefinitions(res.data || []);
      } catch (_) {
        if (!cancelled) setStatusDefinitions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (!currentWorkspace?.id) {
      setStatusFilterPrefsReady(true);
      setSelectedStatuses(null);
      setListSort({ sort: '', order: 'desc' });
      setOrders([]);
      setTotalOrders(0);
      return;
    }
    let cancelled = false;
    setStatusFilterPrefsReady(false);
    setSelectedStatuses(null);
    setListSort({ sort: '', order: 'desc' });
    (async () => {
      try {
        const res = await getUserPreferences(currentWorkspace.id);
        if (!cancelled) {
          const prefs = res.data?.preferences || {};
          setSelectedStatuses(parseStoredFilter(prefs['orders.status_filter'] || ''));
          setListSort(parseStoredListSort(prefs[LIST_SORT_PREF_KEY] || ''));
        }
      } catch (_) {
      } finally {
        if (!cancelled) {
          setStatusFilterPrefsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  const saveStatusFilter = (arr) => {
    if (!currentWorkspace?.id) return;
    clearTimeout(savePrefsTimeout.current);
    savePrefsTimeout.current = setTimeout(() => {
      updateUserPreferences(currentWorkspace.id, {
        'orders.status_filter': serializeFilter(arr),
      }).catch(() => {});
    }, 500);
  };

  const saveListSortPreference = (next) => {
    if (!currentWorkspace?.id) return;
    clearTimeout(saveListSortTimeout.current);
    saveListSortTimeout.current = setTimeout(() => {
      updateUserPreferences(currentWorkspace.id, {
        [LIST_SORT_PREF_KEY]: serializeListSort(next),
      }).catch(() => {});
    }, 500);
  };

  const navigateNewOrder = (typeId) => {
    navigate('/orders/new', { state: { preferredOrderTypeId: typeId } });
  };

  const navigateNewOrderFromImport = () => {
    navigate('/orders/new?import=1', { state: { preferredOrderTypeId: defaultNewOrderTypeId } });
  };

  const listLoading = !statusFilterPrefsReady || loading;

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const loadOrders = useCallback(async () => {
    if (!statusFilterPrefsReady || !currentWorkspace?.id) {
      return;
    }
    try {
      setLoading(true);
      const q = { page, page_size: rowsPerPage };
      if (listSort.sort) {
        q.sort = listSort.sort;
        q.order = listSort.order;
      }
      if (!debouncedSearch) {
        if (selectedStatuses === null) {
        } else if (selectedStatuses.length === 0) {
          q.status_filter = 'none';
        } else {
          q.status = selectedStatuses;
        }
      }
      if (debouncedSearch) {
        q.q = debouncedSearch;
      }
      const response = await getOrders(q);
      setOrders(response.data?.orders || []);
      setTotalOrders(typeof response.data?.total === 'number' ? response.data.total : 0);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    listSort.sort,
    listSort.order,
    page,
    rowsPerPage,
    selectedStatuses,
    statusFilterPrefsReady,
    currentWorkspace?.id,
    debouncedSearch,
  ]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setPage(0);
  }, [selectedStatuses, listSort.sort, listSort.order, debouncedSearch]);

  useEffect(() => {
    if (totalOrders <= 0) {
      return;
    }
    const maxPage = Math.max(0, Math.ceil(totalOrders / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalOrders, rowsPerPage, page]);

  const toggleDeliverySort = () => {
    setListSort((s) => {
      const next =
        s.sort !== 'delivery_date'
          ? { sort: 'delivery_date', order: 'asc' }
          : { sort: 'delivery_date', order: s.order === 'asc' ? 'desc' : 'asc' };
      saveListSortPreference(next);
      return next;
    });
  };

  const allCatalogValues = useMemo(
    () =>
      [...statusDefinitions]
        .sort((a, b) => (a.catalog_sort ?? 0) - (b.catalog_sort ?? 0))
        .map((d) => statusFilterValueFromDefinition(d)),
    [statusDefinitions],
  );

  const statusOptions = useMemo(() => {
    const defs = [...statusDefinitions].sort(
      (a, b) => (a.catalog_sort ?? 0) - (b.catalog_sort ?? 0),
    );
    const base = defs.map((d) => ({
      value: statusFilterValueFromDefinition(d),
      label: d.name || d.id,
    }));
    const byVal = new Map(base.map((x) => [x.value, x]));
    if (Array.isArray(selectedStatuses)) {
      for (const s of selectedStatuses) {
        const v = String(s).trim();
        if (!v || byVal.has(v)) {
          continue;
        }
        byVal.set(v, { value: v, label: v });
      }
    }
    return [...byVal.values()];
  }, [statusDefinitions, selectedStatuses]);

  const effectiveSelected =
    selectedStatuses === null ? [...allCatalogValues] : [...selectedStatuses];

  const allChecked =
    allCatalogValues.length > 0 &&
    allCatalogValues.length === effectiveSelected.length &&
    allCatalogValues.every((v) => effectiveSelected.includes(v));

  const isExplicitFullCatalog =
    selectedStatuses !== null &&
    allCatalogValues.length > 0 &&
    selectedStatuses.length === allCatalogValues.length &&
    allCatalogValues.every((v) => selectedStatuses.includes(v));

  const isFilterActive = selectedStatuses !== null && !isExplicitFullCatalog;

  const applySelection = (next) => {
    const nextArr = [...next];
    const isFull =
      allCatalogValues.length > 0 &&
      nextArr.length === allCatalogValues.length &&
      allCatalogValues.every((v) => nextArr.includes(v));
    if (isFull) {
      setSelectedStatuses(null);
      saveStatusFilter(null);
      return;
    }
    setSelectedStatuses(nextArr);
    saveStatusFilter(nextArr);
  };

  const toggleStatusAll = () => {
    if (allCatalogValues.length === 0) {
      return;
    }
    if (allChecked) {
      applySelection([]);
    } else {
      applySelection(allCatalogValues);
    }
  };

  const toggleStatusOne = (s) => {
    const current = effectiveSelected;
    const next = current.includes(s) ? current.filter((x) => x !== s) : [...current, s];
    applySelection(next);
  };

  const closeStatusQuickMenu = useCallback(() => {
    setStatusQuickMenu(null);
    setStatusQuickDetail(null);
    setStatusQuickLoading(false);
    setStatusQuickSaving(false);
  }, []);

  const openStatusQuickMenu = useCallback(
    async (event, row) => {
      event.preventDefault();
      event.stopPropagation();
      if (row.is_finished) return;
      if (!row.order_type_id) {
        setSnackbar({
          open: true,
          message: translate('orders.list_status_no_type_tooltip'),
          severity: 'info',
        });
        return;
      }
      setStatusQuickMenu({ anchorEl: event.currentTarget, row });
      setStatusQuickDetail(null);
      setStatusQuickLoading(true);
      try {
        const res = await getOrder(row.id);
        const d = res.data;
        if (!d?.status_options?.length) {
          setSnackbar({
            open: true,
            message: translate('orders.list_status_no_transitions'),
            severity: 'info',
          });
          closeStatusQuickMenu();
          return;
        }
        setStatusQuickDetail(d);
      } catch (e) {
        setSnackbar({
          open: true,
          message: e.message || translate('common.error'),
          severity: 'error',
        });
        closeStatusQuickMenu();
      } finally {
        setStatusQuickLoading(false);
      }
    },
    [translate, closeStatusQuickMenu],
  );

  const applyQuickStatus = useCallback(
    async (newStatusId) => {
      if (!statusQuickMenu?.row?.id || !statusQuickDetail) return;
      const orderId = statusQuickMenu.row.id;
      const payload = buildOrderUpdatePayloadFromOrderDetail(statusQuickDetail, {
        status: newStatusId,
        status_transition_draft_note: '',
      });
      if (!payload.delivery_date) {
        setSnackbar({
          open: true,
          message: translate('orders.delivery_date_required'),
          severity: 'error',
        });
        return;
      }
      setStatusQuickSaving(true);
      try {
        const res = await updateOrder(orderId, payload);
        const hid = res.data?.status_history_id;
        if (hid) {
          await migrateOrderDraftMediaToHistory(orderId, hid);
        }
        setSnackbar({
          open: true,
          message: translate('orders.list_status_change_success'),
          severity: 'success',
        });
        closeStatusQuickMenu();
        await loadOrders();
      } catch (e) {
        setSnackbar({
          open: true,
          message: e.message || translate('common.error'),
          severity: 'error',
        });
      } finally {
        setStatusQuickSaving(false);
      }
    },
    [statusQuickMenu, statusQuickDetail, translate, loadOrders, closeStatusQuickMenu],
  );

  const renderOrderStatusChip = (o, extraSx = {}) => {
    const customSx = statusColorChipSx(o.status_color);
    const canQuick = !o.is_finished && Boolean(o.order_type_id);
    let tooltip = translate('orders.list_status_change_tooltip');
    if (o.is_finished) {
      tooltip = translate('orders.list_status_finished_tooltip');
    } else if (!o.order_type_id) {
      tooltip = translate('orders.list_status_no_type_tooltip');
    }
    const mergedSx = {
      ...extraSx,
      ...(customSx || {}),
      ...(canQuick ? { cursor: 'pointer', '&:hover': { opacity: 0.92 } } : {}),
    };
    const rowBusy = statusQuickSaving && statusQuickMenu?.row?.id === o.id;
    return (
      <Tooltip title={tooltip}>
        <span>
          <Chip
            label={o.status_label || o.status}
            size="small"
            onClick={canQuick ? (ev) => void openStatusQuickMenu(ev, o) : undefined}
            disabled={Boolean(rowBusy)}
            aria-haspopup={canQuick ? 'menu' : undefined}
            {...(!customSx ? { color: statusColors[o.status] || 'default' } : {})}
            sx={mergedSx}
          />
        </span>
      </Tooltip>
    );
  };

  const renderPagination = () => (
    <TablePagination
      component="div"
      count={totalOrders}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      disabled={listLoading}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      labelRowsPerPage={translate('orders.pagination_rows_per_page')}
      labelDisplayedRows={({ from, to, count }) =>
        `${from}–${to} ${translate('orders.pagination_of')} ${count}`
      }
    />
  );

  const renderStatusFilterPopover = () => (
    <Popover
      open={Boolean(statusFilterAnchor)}
      anchorEl={statusFilterAnchor}
      onClose={() => setStatusFilterAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ minWidth: 220 }}>
        <List dense disablePadding>
          <ListItemButton onClick={toggleStatusAll} dense>
            <Checkbox
              edge="start"
              checked={allChecked}
              indeterminate={effectiveSelected.length > 0 && !allChecked}
              tabIndex={-1}
              disableRipple
              size="small"
            />
            <ListItemText
              primary={translate('common.all')}
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
          <Divider />
          {statusOptions.map(({ value, label }) => (
            <ListItemButton key={value} onClick={() => toggleStatusOne(value)} dense>
              <Checkbox
                edge="start"
                checked={effectiveSelected.includes(value)}
                tabIndex={-1}
                disableRipple
                size="small"
              />
              <ListItemText primary={label} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Popover>
  );

  const renderStatusQuickMenu = () => (
    <Menu
      anchorEl={statusQuickMenu?.anchorEl ?? null}
      open={Boolean(statusQuickMenu)}
      onClose={closeStatusQuickMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { minWidth: 200 } }}
      MenuListProps={{ dense: true }}
    >
      {statusQuickLoading ? (
        <MenuItem disabled>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          {translate('orders.list_status_menu_loading')}
        </MenuItem>
      ) : (
        (statusQuickDetail?.status_options || []).map((opt) => (
          <MenuItem
            key={opt.id}
            disabled={statusQuickSaving}
            onClick={() => void applyQuickStatus(opt.id)}
          >
            {opt.name}
          </MenuItem>
        ))
      )}
    </Menu>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {orderTypesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 36 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activeOrderTypes.length === 0 ? (
            <Button variant="contained" startIcon={<AddIcon />} disabled sx={ORDERS_TOOLBAR_BUTTON_SX}>
              {translate('common.new')} {translate('orders.title')}
            </Button>
          ) : (
            <>
              <Box
                component="span"
                role="group"
                aria-label={translate('orders.new_order_split_aria')}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
              >
                {primaryOrderTypes.map((t, index) => (
                  <Button
                    key={t.id}
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigateNewOrder(t.id)}
                    data-testid={index === 0 ? 'orders-new-primary' : undefined}
                    sx={ORDERS_TOOLBAR_BUTTON_SX}
                  >
                    {t.name}
                  </Button>
                ))}
                <Button
                  variant="contained"
                  aria-label={translate('orders.new_order_open_types_menu')}
                  data-testid="orders-new-menu-trigger"
                  onClick={(e) => setNewOrderMenuAnchor(e.currentTarget)}
                  sx={{ ...ORDERS_TOOLBAR_BUTTON_SX, minWidth: 36, px: 1 }}
                >
                  <AddIcon />
                </Button>
              </Box>
              <Menu
                anchorEl={newOrderMenuAnchor}
                open={Boolean(newOrderMenuAnchor)}
                onClose={() => setNewOrderMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {overflowOrderTypes.map((t) => (
                  <MenuItem
                    key={t.id}
                    onClick={() => {
                      setNewOrderMenuAnchor(null);
                      navigateNewOrder(t.id);
                    }}
                  >
                    {t.name}
                  </MenuItem>
                ))}
                {overflowOrderTypes.length > 0 ? <Divider /> : null}
                <MenuItem
                  onClick={() => {
                    setNewOrderMenuAnchor(null);
                    navigateNewOrderFromImport();
                  }}
                >
                  {translate('orders.import_from_order_menu')}
                </MenuItem>
              </Menu>
            </>
          )}
          {currentWorkspace ? (
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              aria-label={translate('orders.copy_quick_intake_link')}
              sx={ORDERS_TOOLBAR_BUTTON_SX}
              onClick={async () => {
                const qs = new URLSearchParams({ lang: language || 'pt-BR' });
                const url = `${window.location.origin}/solicitacao/${encodeURIComponent(currentWorkspace.id)}?${qs.toString()}`;
                try {
                  await navigator.clipboard.writeText(url);
                  setSnackbar({
                    open: true,
                    message: translate('orders.copy_quick_intake_copied'),
                    severity: 'success',
                  });
                } catch {
                  setSnackbar({
                    open: true,
                    message: translate('orders.copy_quick_intake_failed'),
                    severity: 'error',
                  });
                }
              }}
            >
              {translate('orders.copy_quick_intake_button')}
            </Button>
          ) : null}
        </Box>
      </Box>

      <ListToolbar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchHelperTextKey="orders.list_search_hint"
      />

      {isMobile ? (
        <>
          {/* Mobile: filter bar */}
          <Box sx={{ mb: 1.5 }}>
            <Button
              size="small"
              startIcon={isFilterActive ? <FilterListOffIcon /> : <FilterListIcon />}
              color={isFilterActive ? 'primary' : 'inherit'}
              variant={isFilterActive ? 'outlined' : 'text'}
              onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
            >
              {translate('orders.status')}
              {isFilterActive ? ` (${effectiveSelected.length})` : ''}
            </Button>
          </Box>

          {/* Mobile: card list */}
          {listLoading ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress />
            </Paper>
          ) : orders.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">{translate('orders.no_orders')}</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {orders.map((o) => {
                const coverSrc = o.cover_thumb_path ? `${ORDERS_API_URL}${o.cover_thumb_path}` : PRODUCT_DEFAULT_IMAGE;
                return (
                  <Paper key={o.id} sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 0.75 }}>
                      <Avatar
                        variant="rounded"
                        src={coverSrc}
                        alt=""
                        sx={{ width: 56, height: 56, flexShrink: 0, cursor: 'pointer' }}
                        onClick={() => navigate(`/orders/${o.id}/edit`)}
                        imgProps={{
                          onError: (e) => {
                            e.currentTarget.src = PRODUCT_DEFAULT_IMAGE;
                          },
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {o.number}
                            </Typography>
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                              {o.title || '—'}
                            </Typography>
                            {o.customer_name && (
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {o.customer_name}
                              </Typography>
                            )}
                          </Box>
                          {renderOrderStatusChip(o, { ml: 1, flexShrink: 0 })}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {translate('orders.delivery_date')}: {o.delivery_date ? formatDate(o.delivery_date) : '—'}
                      </Typography>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title={translate('common.edit')}>
                          <IconButton size="small" onClick={() => navigate(`/orders/${o.id}/edit`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {o.can_soft_delete && (
                          <Tooltip title={translate('orders.soft_delete_from_list')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPendingSoftDelete({ id: o.id, number: o.number });
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}

          {renderPagination()}
        </>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }}>{translate('orders.list_cover')}</TableCell>
                <TableCell>{translate('orders.number')}</TableCell>
                <TableCell>{translate('orders.order_title')}</TableCell>
                <TableCell>{translate('orders.customer_id')}</TableCell>
                <TableCell>{translate('orders.order_type')}</TableCell>
                <TableCell>
                  <Tooltip title={translate('orders.list_items_quantity_hint')}>
                    <span>{translate('orders.list_items_quantity')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      component="span"
                      sx={isFilterActive ? { fontWeight: 700, color: 'primary.main' } : {}}
                    >
                      {translate('orders.status')}
                    </Box>
                    {isFilterActive && (
                      <Chip
                        label={effectiveSelected.length}
                        size="small"
                        color="primary"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer' }}
                        onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
                      />
                    )}
                    <Tooltip title={isFilterActive ? translate('orders.filter_active') : translate('orders.filter_by_status')}>
                      <IconButton
                        size="small"
                        onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
                        sx={{ p: 0.25 }}
                        color={isFilterActive ? 'primary' : 'default'}
                      >
                        {isFilterActive ? <FilterListOffIcon fontSize="small" /> : <FilterListIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sortDirection={listSort.sort === 'delivery_date' ? listSort.order : false}>
                  <TableSortLabel
                    active={listSort.sort === 'delivery_date'}
                    direction={listSort.sort === 'delivery_date' ? listSort.order : 'asc'}
                    onClick={toggleDeliverySort}
                  >
                    {translate('orders.delivery_date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{translate('orders.created_at')}</TableCell>
                <TableCell align="right">{translate('orders.list_final_value')}</TableCell>
                <TableCell align="right">{translate('orders.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {translate('common.loading')}
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {translate('orders.no_orders')}
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((o) => {
                  const coverSrc = o.cover_thumb_path ? `${ORDERS_API_URL}${o.cover_thumb_path}` : PRODUCT_DEFAULT_IMAGE;
                  return (
                    <TableRow key={o.id}>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Avatar
                          variant="rounded"
                          src={coverSrc}
                          alt=""
                          sx={{ width: 48, height: 48, cursor: 'pointer' }}
                          component={RouterLink}
                          to={`/orders/${o.id}/edit`}
                          imgProps={{
                            onError: (e) => {
                              e.currentTarget.src = PRODUCT_DEFAULT_IMAGE;
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell padding="none">
                        <Link
                          component={RouterLink}
                          to={`/orders/${o.id}/edit`}
                          underline="hover"
                          color="inherit"
                          title={translate('common.edit')}
                          aria-label={`${translate('common.edit')}, ${translate('orders.number')} ${o.number}`}
                          sx={orderEditCellLinkSx}
                        >
                          {o.number}
                        </Link>
                      </TableCell>
                      <TableCell padding="none">
                        <Link
                          component={RouterLink}
                          to={`/orders/${o.id}/edit`}
                          underline="hover"
                          color="inherit"
                          title={translate('common.edit')}
                          aria-label={`${translate('common.edit')}, ${translate('orders.order_title')}: ${o.title || '—'}`}
                          sx={orderEditCellLinkSx}
                        >
                          {o.title || '—'}
                        </Link>
                      </TableCell>
                      <TableCell>{o.customer_name || o.customer_id || '—'}</TableCell>
                      <TableCell>{o.order_type_name || '—'}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {Number.isFinite(Number(o.line_items_count)) ? Number(o.line_items_count) : 0}
                      </TableCell>
                      <TableCell>{renderOrderStatusChip(o)}</TableCell>
                      <TableCell>{o.delivery_date ? formatDate(o.delivery_date) : '—'}</TableCell>
                      <TableCell>{formatDateTime(o.created_at) || '-'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatCurrency(
                          Number.isFinite(Number(o.final_value)) ? Number(o.final_value) : 0,
                        ) || '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={translate('common.edit')}>
                          <IconButton sx={{ p: '10px' }} onClick={() => navigate(`/orders/${o.id}/edit`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {o.can_soft_delete ? (
                          <Tooltip title={translate('orders.soft_delete_from_list')}>
                            <IconButton
                              sx={{ p: '10px' }}
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPendingSoftDelete({ id: o.id, number: o.number });
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {renderPagination()}
        </TableContainer>
      )}

      {renderStatusFilterPopover()}
      {renderStatusQuickMenu()}

      <ConfirmDialog
        open={Boolean(pendingSoftDelete)}
        title={translate('common.confirm_delete_title')}
        message={translate('orders.confirm_soft_delete_order')}
        onConfirm={async () => {
          const row = pendingSoftDelete;
          setPendingSoftDelete(null);
          if (!row?.id) return;
          try {
            await softDeleteOrder(row.id);
            setSnackbar({ open: true, message: translate('orders.soft_delete_success'), severity: 'success' });
            await loadOrders();
          } catch (e) {
            setSnackbar({
              open: true,
              message: e.message || translate('common.error'),
              severity: 'error',
            });
          }
        }}
        onCancel={() => setPendingSoftDelete(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrdersPage;
